import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Brain, Plus, Trash2, FileText } from 'lucide-react'
import { useDecisionAnalysisStore } from '../stores/decisionAnalysisStore'
import { analyzeDecisionByType } from '../services/api'
import { useAIUsageStore } from '../stores/aiUsageStore'
import { useAuthStore } from '../stores/authStore'
import { DecisionTemplates } from '../components/DecisionTemplates'
import { CausalChainAnalysis } from '../components/CausalChainAnalysis'
import { AnalysisProgress } from '../components/AnalysisProgress'

export const CausalChainAnalysisPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const {
    decisionTitle,
    decisionDescription,
    options,
    isAnalyzing,
    causalChainResult,
    analysisSteps,
    currentAnalysisStep,
    setDecisionTitle,
    setDecisionDescription,
    addOption,
    removeOption,
    updateOption,
    setIsAnalyzing,
    setCausalChainResult,
    resetDecision,
    setOptions,
    initializeCausalChainSteps,
    updateAnalysisStep,
    setCurrentAnalysisStep
  } = useDecisionAnalysisStore()

  const { user } = useAuthStore()
  const { canUseAI, incrementUsage, getRemainingUsage } = useAIUsageStore()
  const [showTemplates, setShowTemplates] = useState(false)

  const startAnalysis = async () => {
    if (!user && !canUseAI()) {
      alert(`You have ${getRemainingUsage()} AI calls remaining. Please sign in for unlimited access.`)
      return
    }

    setIsAnalyzing(true)
    initializeCausalChainSteps()
    
    try {
      if (!user) {
        incrementUsage()
      }

      console.log('=== Starting Causal Chain Analysis ===')
      console.log('Request data:', {
        decisionTitle,
        decisionDescription,
        options
      })

      // Simulate step-by-step progress
      const steps = [
        { id: 'step1', delay: 1000 },
        { id: 'step2', delay: 1500 },
        { id: 'step3', delay: 2000 },
        { id: 'step4', delay: 1500 },
        { id: 'step5', delay: 1500 },
        { id: 'step6', delay: 1000 }
      ]

      for (const step of steps) {
        updateAnalysisStep(step.id, 'active')
        setCurrentAnalysisStep(step.id)
        await new Promise(resolve => setTimeout(resolve, step.delay))
        updateAnalysisStep(step.id, 'completed')
      }

      const result = await analyzeDecisionByType('causal-chain', {
        decisionTitle,
        decisionDescription,
        options
      })

      console.log('=== API Response Received ===')
      console.log('Raw API result:', result)
      console.log('Result type:', typeof result)

      // Handle different response formats
      let analysisData = null
      if (result && typeof result === 'object') {
        // If result has an analysis property, use that
        if (result.analysis) {
          console.log('Found analysis property in result')
          analysisData = result.analysis
        } else if (result.success && result.data) {
          // If result has success and data properties
          console.log('Found success and data properties in result')
          analysisData = result.data
        } else {
          // Otherwise, use the result directly
          console.log('Using result directly')
          analysisData = result
        }
      }

      console.log('=== Final Analysis Data ===')
      console.log('Analysis data to be set:', analysisData)
      console.log('Analysis data type:', typeof analysisData)
      
      setCausalChainResult(analysisData)
    } catch (error) {
      console.error('Analysis failed:', error)
      alert('Analysis failed. Please try again.')
    } finally {
      setIsAnalyzing(false)
    }
  }

  const handleTemplateSelect = (template: any) => {
    setDecisionTitle(template.title)
    setDecisionDescription(template.fullDescription)

    const templateOptions = template.options.map((opt: string) => ({
      id: Date.now().toString() + Math.random(),
      text: opt,
      pros: [],
      cons: [],
      score: 0
    }))
    setOptions(templateOptions)
    setShowTemplates(false)
  }

  const renderAnalysisForm = () => (
    <div className="space-y-8">
      {/* Decision Definition */}
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">What decision are you trying to make?</h3>
          <input
            type="text"
            value={decisionTitle}
            onChange={(e) => setDecisionTitle(e.target.value)}
            placeholder="e.g., Should I invest in this startup?"
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Provide more context</h3>
          <textarea
            value={decisionDescription}
            onChange={(e) => setDecisionDescription(e.target.value)}
            placeholder="Describe your situation, goals, and any relevant factors..."
            rows={4}
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Options */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">What are your options?</h3>
          <button
            onClick={addOption}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Option</span>
          </button>
        </div>
        <div className="space-y-4">
          {options.map((option, index) => (
            <div key={option.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-500">Option {index + 1}</span>
                <button
                  onClick={() => removeOption(option.id)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <input
                type="text"
                value={option.text}
                onChange={(e) => updateOption(option.id, 'text', e.target.value)}
                placeholder="Describe this option..."
                className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          ))}
          {options.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <Brain className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Add your decision options to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Analysis Button */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-green-900 mb-2">Ready for Causal Chain Analysis</h3>
        <p className="text-green-800 mb-4">
          We'll analyze the causal chain of your decision, showing up to 3 levels of potential outcomes and their impacts.
        </p>
        
        <div className="space-y-4">
          <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <h4 className="font-medium text-gray-900 mb-2">Decision Summary</h4>
            <p className="text-gray-600 mb-2"><strong>Title:</strong> {decisionTitle || 'Not specified'}</p>
            <p className="text-gray-600 mb-2"><strong>Description:</strong> {decisionDescription || 'Not specified'}</p>
            <p className="text-gray-600"><strong>Options:</strong> {options.length}</p>
          </div>
          
          <button
            onClick={startAnalysis}
            disabled={isAnalyzing || options.length === 0}
            className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isAnalyzing ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <Brain className="w-5 h-5" />
                <span>Start Causal Chain Analysis</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )

  const handleShowTemplates = () => {
    resetDecision()
    setShowTemplates(true)
  }

  const handleNewAnalysis = () => {
    resetDecision()
    setShowTemplates(false)
  }

  return (
    <>
      <Helmet>
        <title>Causal Chain Analysis - Free AI Decision Maker | Online Decision Assistant</title>
        <meta name="description" content="Use our free AI decision maker to analyze causal chains and decision outcomes. Map decision consequences with up to 3 levels of tree structure. Free online decision assistant." />
        <meta name="keywords" content="causal chain analysis, ai decision maker, decision outcomes, decision consequences, decision mapping, ai decision analysis, free decision tool" />
        <meta property="og:title" content="Causal Chain Analysis - Free AI Decision Maker" />
        <meta property="og:description" content="Use our free AI decision maker to analyze causal chains and decision outcomes. Map decision consequences with tree structure." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://aidecision.org/decision-analysis/causal-chain" />
        <meta name="twitter:title" content="Causal Chain Analysis - Free AI Decision Maker" />
        <meta name="twitter:description" content="Use our free AI decision maker to analyze causal chains and decision outcomes." />
        <link rel="canonical" href="https://aidecision.org/decision-analysis/causal-chain" />
      </Helmet>

      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Causal Chain Analysis</h1>
              <p className="text-gray-600">
                Show the performance of decision results in a causal chain, up to 3 levels of tree structure
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handleNewAnalysis}
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="w-4 h-4" /><span>New</span>
              </button>
              <button
                onClick={handleShowTemplates}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <FileText className="w-4 h-4" /><span>Templates</span>
              </button>
            </div>
          </div>
        </div>

        {showTemplates ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <DecisionTemplates onSelectTemplate={handleTemplateSelect} />
          </div>
        ) : isAnalyzing ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <AnalysisProgress 
              steps={analysisSteps}
              currentStep={currentAnalysisStep}
              analysisType="causal-chain"
            />
          </div>
        ) : causalChainResult ? (
          <CausalChainAnalysis 
            result={causalChainResult}
            isLoading={false}
          />
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {renderAnalysisForm()}
          </div>
        )}
      </div>
    </>
  )
} 